/* Share Grid — header + emoji rows + footer + Copy buttons. The emoji
   block grows with play (a single ⬜ row before any move; afterward only
   the used rows). Reads marksByGuess and hintPurchasedAt (both signals),
   so updates are automatic.

   Two clipboard buttons sit below the social-share row:
     - Text: copies the same emoji-grid string the OG single-button used
       to write — preserves history-paste muscle memory.
     - Image: copies a canvas-rendered PNG of the same grid (colored
       squares, theme-aware background) so a paste into Slack / Messages
       /etc. shows up as a pretty share card. */

import { useEffect, useRef, useState } from "preact/hooks";
import {
  guesses,
  HINT_COST,
  hintPurchasedAt,
  marksByGuess,
  puzzle,
  puzzleNumber,
  SPACES_COST,
  spacesPurchased,
  spacesPurchasedAt,
  status,
  statusLine,
} from "../game/state";
import { buildShareGrid, buildShareText } from "../game/share";
import { renderShareImageBlob } from "../game/shareImage";
import { theme } from "../theme";
import { ShareButtons } from "./ShareButtons";

type CopyState = "idle" | "text" | "image";

export function ShareGrid() {
  const allMarks = marksByGuess.value;
  const p = puzzle.value;
  // Don't render the share grid until a puzzle is loaded — its
  // dimensions and emoji rows are computed off the solution.
  if (p == null) return null;
  // When the player hasn't bought Spaces, render the share grid in the
  // same bare-tile shape the board shows: letters-only, no spacing or
  // punctuation. `solutionNormalized` is letters-only lowercase; case
  // doesn't matter (every letter renders to an emoji), so it's a clean
  // proxy for "the bare mask".
  const renderText = spacesPurchased.value ? p.text : p.solutionNormalized;
  const text = buildShareText(
    allMarks,
    puzzleNumber.value,
    renderText,
    p.maxGuesses,
    hintPurchasedAt.value,
    HINT_COST,
    spacesPurchasedAt.value,
    SPACES_COST,
    status.value === "won",
  );
  const lines = text.split("\n");
  const header = lines[0]!;
  const footer = lines[lines.length - 1]!;
  const rows = lines.slice(1, -1);
  const [copied, setCopied] = useState<CopyState>("idle");

  // Keep the most recent guess visible as the player commits new ones;
  // the rows container is overflow-scrolled so older guesses go offscreen
  // upward. The dep on rows.length re-runs after each commit.
  const rowsRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = rowsRef.current;
    if (el == null) return;
    el.scrollTop = el.scrollHeight;
  }, [rows.length]);

  // Compose the clipboard-text payload (text + dev-mode appends + the
  // canonical site URL). Centralized so both Copy paths stay in sync —
  // the image variant uses the same "what did the player share" data
  // model as the text variant; the only differences are the format and
  // the rendering target.
  const composeCopyText = (): string => {
    let copyText = text;
    let ribbonVisible = false;
    if (import.meta.env.DEV) {
      // The dev ribbon's visibility (Escape toggles it) decides whether
      // dev-only appends ride along on Copy. Hidden ribbon → dev mode is
      // out of the way and the clipboard payload matches what a player
      // would copy in production. Visible ribbon → the dev gets the
      // playing-state keyboard summary or the terminal-state solution
      // reveal, depending on game status.
      ribbonVisible =
        typeof document !== "undefined" &&
        document.body.classList.contains("body--has-dev-ribbon");
      if (ribbonVisible) {
        if (status.value === "playing") {
          const debug = (globalThis as typeof globalThis & {
            __PD_KEYBOARD_DEBUG__?: (
              guessRows: readonly (readonly (string | null)[])[],
              marks: readonly (readonly import("../game/scoring").Mark[])[],
              status: "playing" | "won" | "lost",
            ) => string | null;
          }).__PD_KEYBOARD_DEBUG__;
          if (debug != null) {
            const append = debug(
              guesses.value,
              marksByGuess.value,
              "playing",
            );
            if (append != null) copyText = `${text}\n\n${append}`;
          }
        } else {
          copyText = `${text}\n${p.text}\n`;
        }
      }
    }
    // Production and dev-ribbon-hidden: stamp the canonical site URL
    // as the last line so a shared / pasted Share Grid always carries
    // a clickable "where do I play this" pointer. With the dev ribbon
    // visible, the dev appends are the trailing context instead.
    if (!ribbonVisible) {
      copyText = `${copyText}\nhttps://palindragon.com`;
    }
    return copyText;
  };

  const flashCopied = (which: Exclude<CopyState, "idle">) => {
    setCopied(which);
    window.setTimeout(() => setCopied("idle"), 1500);
  };

  const onCopyText = async () => {
    try {
      await navigator.clipboard.writeText(composeCopyText());
      flashCopied("text");
    } catch {
      /* Clipboard API may be blocked (insecure context, denied permission).
         Silently leave the button label unchanged rather than misleading. */
    }
  };

  const onCopyImage = async () => {
    // Build the structured grid fresh (signals already evaluated above
    // would also work, but reading via the same args is symmetric with
    // buildShareText). The image carries the same content as the text
    // version — just rendered as colored squares on a themed card.
    const grid = buildShareGrid(
      allMarks,
      puzzleNumber.value,
      renderText,
      p.maxGuesses,
      hintPurchasedAt.value,
      HINT_COST,
      spacesPurchasedAt.value,
      SPACES_COST,
      status.value === "won",
    );
    try {
      // Safari requires the ClipboardItem value to be a Promise<Blob> when
      // the blob is generated asynchronously, so the user-gesture click
      // handler can return synchronously while the encode finishes.
      // Chrome/Firefox accept either a Blob or a Promise<Blob>.
      const blobPromise = renderShareImageBlob(grid, theme.value);
      const item = new ClipboardItem({ "image/png": blobPromise });
      await navigator.clipboard.write([item]);
      flashCopied("image");
    } catch {
      /* Clipboard image-write may be unsupported (older Safari, Firefox
         without the dom.events.asyncClipboard.clipboardItem pref) or
         blocked. No fallback yet — the Text button still works. */
    }
  };

  return (
    <section class="sharegrid" aria-label="Share Grid">
      <div class="sharegrid__header">{header}</div>
      <div
        class="sharegrid__rows"
        aria-label="Guesses in emoji form"
        ref={rowsRef}
      >
        {rows.map((row, i) => (
          <div class="sharegrid__row" key={i}>
            {row}
          </div>
        ))}
      </div>
      <div class="sharegrid__hintline">{footer}</div>
      <ShareButtons
        text={text}
        status={`PalinDragon #${puzzleNumber.value} — ${statusLine.value}`}
      />
      <div class="sharegrid__copyrow">
        <span class="sharegrid__copyrow-label">Copy to Clipboard:</span>
        <button
          type="button"
          class="sharegrid__button"
          onClick={onCopyText}
          aria-label="Copy share grid as text"
        >
          {copied === "text" ? "Copied!" : "Text"}
        </button>
        <button
          type="button"
          class="sharegrid__button"
          onClick={onCopyImage}
          aria-label="Copy share grid as image"
        >
          {copied === "image" ? "Copied!" : "Image"}
        </button>
      </div>
    </section>
  );
}
